import {
  PageStructure,
  SelectedElementContainer,
  toDragPointerEvent,
  useLinearTransform,
  useUpdateBounds,
  ViewElementContainer
} from '@st/document-viewer-kit'
import { ElementStatus, ToolMessage } from '@st/document-viewer-kit/plugins/annotations'
import { CommentToolState } from '../../annotations'
import { useAnnotationsContext } from '../../annotations-context'
import { DocumentAnnotationDisplay } from '../../document-annotation-display'
import { toBoundedCommentAnnotation, type ViewerCommentAnnotation } from '../../viewer-annotations'

const COMMENT_SIZE = 20

export function CommentToolOverlay({
  page,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: CommentToolState
  activeAnnotation: ViewerCommentAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<ViewerCommentAnnotation, CommentToolState>) => void
}) {
  const { currentUser } = useAnnotationsContext()

  const transform = useLinearTransform()

  return (
    <div
      style={{
        position: 'absolute',
        cursor: 'copy',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.0)'
      }}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')

        if (activeAnnotation) {
          send({ type: 'deselectAnnotation' })
          return
        } else {
          send({
            type: 'requestAnnotationDraft',
            elementStatus: 'editing',
            annotationDraft: {
              authorId: currentUser?.id,
              type: 'comment',
              pageNumber: page.pageNumber,
              content: '',
              readOnly: false,
              bounds: {
                x: pointerEvent.point.x,
                y: pointerEvent.point.y,
                width: COMMENT_SIZE,
                height: COMMENT_SIZE
              }
            }
          })
        }
      }}
    />
  )
}

export function CommentAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: ViewerCommentAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<ViewerCommentAnnotation, CommentToolState>) => void
}) {
  switch (status) {
    case 'selected':
    case 'editing':
      if (annotation.readOnly) {
        return (
          <SelectedElementContainer
            bounds={requireCommentBounds(annotation)}
            draggable={false}
            resizable={false}
          >
            <DocumentAnnotationDisplay annotation={toBoundedCommentAnnotation(annotation)} />
          </SelectedElementContainer>
        )
      }
      return <CommentAnnotationSelected annotation={annotation} send={send} />
    case 'none':
      return (
        <CommentAnnotationView
          annotation={annotation}
          onSelect={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
        />
      )
  }
}

function CommentAnnotationSelected({
  annotation,
  send
}: {
  annotation: ViewerCommentAnnotation
  send: (message: ToolMessage<ViewerCommentAnnotation, CommentToolState>) => void
}) {
  const { bounds, onMove } = useUpdateBounds({
    initialBounds: requireCommentBounds(annotation),
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  return (
    <SelectedElementContainer bounds={bounds} onMove={onMove} resizable={false}>
      <DocumentAnnotationDisplay annotation={toBoundedCommentAnnotation(annotation)} />
    </SelectedElementContainer>
  )
}

function CommentAnnotationView({
  annotation,
  onSelect
}: {
  annotation: ViewerCommentAnnotation
  onSelect: () => void
}) {
  return (
    <ViewElementContainer bounds={requireCommentBounds(annotation)} onClick={onSelect}>
      <DocumentAnnotationDisplay annotation={toBoundedCommentAnnotation(annotation)} />
    </ViewElementContainer>
  )
}

function requireCommentBounds(annotation: ViewerCommentAnnotation) {
  if (!annotation.bounds) throw new Error(`Comment ${annotation.id} has not been positioned`)
  return annotation.bounds
}
